import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ColumnTable,
  Icon,
  Table,
  showDialogConfirm,
} from '@octano/global-ui';
import { Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { SectionTitle } from '../../../../../components/text';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';
import { TranslationsKeys } from '../../../../../locales/translations';
import { CourseFormFields, CourseGroupingPick } from '../../types';
import AddCourseGroupingModal from '../modals/AddCourseGroupingModal';

type Props = { readOnly?: boolean };

const CourseGroupingsSection = ({ readOnly = false }: Props) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { control } = useFormContext<CourseFormFields>();

  // useFieldArray manages the array's length correctly on remove/replace.
  // Plain setValue('courseGroups', next) on an array hydrated via reset
  // does per-index merge in rhf v7, which leaves stale tail entries when
  // shrinking. fields/append/remove avoid that pitfall.
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'courseGroups',
    keyName: '_rhfKey',
  });

  const [open, setOpen] = useState<boolean>(false);

  const courseGroups: CourseGroupingPick[] = useMemo(
    () =>
      fields.map((f) => ({
        id: f.id,
        name: f.name,
        code: f.code,
        school: f.school ?? null,
      })),
    [fields],
  );

  const excludeIds = useMemo(() => courseGroups.map((g) => g.id), [
    courseGroups,
  ]);

  const addGrouping = useCallback(
    (g: CourseGroupingPick) => {
      append(g);
    },
    [append],
  );

  const removeGroupingById = useCallback(
    (id: number) => {
      const idx = fields.findIndex((f) => f.id === id);
      if (idx >= 0) {
        remove(idx);
      }
    },
    [fields, remove],
  );

  const askRemove = useCallback(
    (row: CourseGroupingPick) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t('groupings.removeConfirm.title'),
        subtitle: t('groupings.removeConfirm.subtitle', { name: row.name }),
        btnConfirm: {
          text: t('common:actions.confirm'),
          onConfirm: () => removeGroupingById(row.id),
        },
        btnClose: { text: t('common:actions.cancel') },
      });
    },
    [removeGroupingById, t],
  );

  const columns = useMemo((): ColumnTable<CourseGroupingPick>[] => {
    const base: ColumnTable<CourseGroupingPick>[] = [
      {
        columnName: 'name',
        width: '35%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t('groupings.columns.name'),
      },
      {
        columnName: 'code',
        width: '25%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t('groupings.columns.id'),
      },
      {
        columnName: 'school',
        width: '30%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t('groupings.columns.school'),
        cellFormat: ({ row }) => <>{row.school?.name ?? '-'}</>,
      },
    ];

    if (!readOnly) {
      base.push({
        columnName: 'id',
        width: '10%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: t('groupings.columns.actions'),
        cellFormat: ({ row }) => (
          <div className="cursor-pointer" onClick={() => askRemove(row)}>
            <Icon size="15" name="trash" color="primary" />
          </div>
        ),
      });
    }

    return base;
  }, [t, readOnly, askRemove]);

  return (
    <>
      <Row>
        <Col
          xs={12}
          className="d-flex justify-content-between align-items-center p-4"
        >
          <SectionTitle text={t('groupings.title')} />
          {!readOnly && (
            <Button
              icon="plus"
              onClick={() => setOpen(true)}
              size="md"
              text={t('groupings.addButton')}
            />
          )}
        </Col>
        <Col xs={12} className="px-4">
          <p className="fs-16 fw-300">{t(`groupings.description`)}</p>
        </Col>
        <Col xs={12}>
          <Table
            columns={columns}
            data={courseGroups}
            noResultsText={
              <TableEmptyContent
                title={t('groupings.emptyState')}
                subtitle=""
              />
            }
          />
        </Col>
      </Row>

      <AddCourseGroupingModal
        isOpen={open}
        onClose={() => setOpen(false)}
        excludeIds={excludeIds}
        onConfirm={addGrouping}
      />
    </>
  );
};

export default CourseGroupingsSection;
